import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import certificateService from "../../../services/certificateService";
import Loader from "../Loader";

export default function VerifyCertificate() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const doVerify = async () => {
      if (!code) {
        setError("Verification code is missing.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await certificateService.verifyCertificate(code);
        setResult(data);
      } catch (err) {
        console.error("Verification error:", err);
        if (err?.response?.data) {
          setResult(err.response.data);
        } else {
          setError(err.message || "Failed to verify certificate.");
        }
      } finally {
        setLoading(false);
      }
    };

    doVerify();
  }, [code]);

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 1rem",
        fontFamily: "var(--body-font, sans-serif)",
      }}
    >
      {/* Brand Header */}
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            marginBottom: "0.5rem",
          }}
          title="GradeMe AI"
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/imgs/logo.svg`}
            alt="GradeMe AI"
            style={{
              width: "200px",
              height: "auto",
              display: "block",
            }}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "https://res.cloudinary.com/dfdsncmih/image/upload/v1761210947/grademe_ai_logo_crp_vrjuhf.png";
            }}
          />
        </Link>
        <p
          style={{
            margin: 0,
            fontSize: "0.88rem",
            color: "#64748b",
            fontWeight: "500",
          }}
        >
          Certificate Verification Portal
        </p>
      </div>

      {/* Main Verification Card */}
      <div
        style={{
          width: "100%",
          maxWidth: "620px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow:
            "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
            <Loader
              style={{ fontSize: "36px", marginBottom: "1rem", left: "0" }}
            />
            <h3
              style={{
                margin: "1rem 0 0.5rem 0",
                color: "#1e293b",
                fontSize: "1.1rem",
              }}
            >
              Verifying Certificate Authenticity...
            </h3>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem" }}>
              Code:{" "}
              <span style={{ fontFamily: "monospace", fontWeight: "600" }}>
                {code}
              </span>
            </p>
          </div>
        ) : error || (!result?.verified && !result?.revoked) ? (
          /* ── Invalid State ── */
          <div style={{ padding: "3rem 2rem", textAlign: "center" }}>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.8rem",
                marginBottom: "1.25rem",
              }}
            >
              <i className="fa-solid fa-xmark" />
            </div>

            <h2
              style={{
                margin: "0 0 0.5rem 0",
                fontSize: "1.4rem",
                fontWeight: "700",
                color: "#991b1b",
              }}
            >
              Invalid Certificate
            </h2>
            <p
              style={{
                margin: "0 auto 1.5rem auto",
                color: "#475569",
                fontSize: "0.92rem",
                maxWidth: "400px",
                lineHeight: "1.5",
              }}
            >
              {result?.message ||
                error ||
                "No matching certificate record was found for this verification code."}
            </p>

            <div
              style={{
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
                padding: "0.75rem",
                display: "inline-block",
                marginBottom: "1.5rem",
                border: "1px solid #e2e8f0",
              }}
            >
              <span
                style={{
                  fontSize: "0.8rem",
                  color: "#64748b",
                  marginRight: "6px",
                }}
              >
                Scanned Code:
              </span>
              <code
                style={{
                  fontSize: "0.9rem",
                  color: "#0f172a",
                  fontWeight: "700",
                }}
              >
                {code}
              </code>
            </div>

            <div>
              <Link
                to="/"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  backgroundColor: "#0f172a",
                  color: "#ffffff",
                  fontSize: "0.88rem",
                  fontWeight: "600",
                  textDecoration: "none",
                }}
              >
                Back to GradeMe AI
              </Link>
            </div>
          </div>
        ) : result?.revoked ? (
          /* ── Revoked State ── */
          <div style={{ padding: "3rem 2rem", textAlign: "center" }}>
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.8rem",
                marginBottom: "1.25rem",
              }}
            >
              <i className="fa-solid fa-triangle-exclamation" />
            </div>

            <h2
              style={{
                margin: "0 0 0.5rem 0",
                fontSize: "1.4rem",
                fontWeight: "700",
                color: "#b91c1c",
              }}
            >
              Certificate Revoked
            </h2>
            <p
              style={{
                margin: "0 auto 1.5rem auto",
                color: "#475569",
                fontSize: "0.92rem",
                maxWidth: "420px",
                lineHeight: "1.5",
              }}
            >
              This certificate was officially revoked by the issuing institution
              and is no longer valid.
            </p>

            <div
              style={{
                backgroundColor: "#fff1f2",
                border: "1px solid #fecdd3",
                borderRadius: "10px",
                padding: "1rem",
                textAlign: "left",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "6px",
                  fontSize: "0.85rem",
                }}
              >
                <span style={{ color: "#881337", fontWeight: "600" }}>
                  Status:
                </span>
                <span style={{ color: "#be123c", fontWeight: "700" }}>
                  Revoked
                </span>
              </div>
              {result.revokedAt && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                    fontSize: "0.85rem",
                  }}
                >
                  <span style={{ color: "#881337", fontWeight: "600" }}>
                    Revoked Date:
                  </span>
                  <span style={{ color: "#4c0519" }}>
                    {new Date(result.revokedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {result.message && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    marginTop: "8px",
                    borderTop: "1px dashed #fda4af",
                    paddingTop: "6px",
                  }}
                >
                  <span style={{ color: "#881337", fontWeight: "600" }}>
                    Reason:{" "}
                  </span>
                  <span style={{ color: "#4c0519" }}>{result.message}</span>
                </div>
              )}
            </div>

            <Link
              to="/"
              style={{
                display: "inline-block",
                padding: "10px 20px",
                borderRadius: "8px",
                backgroundColor: "#0f172a",
                color: "#ffffff",
                fontSize: "0.88rem",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              Back to Home
            </Link>
          </div>
        ) : (
          /* ── Verified (Success) State ── */
          <div>
            {/* Top Verified Header Bar */}
            <div
              style={{
                backgroundColor: "#ecfdf5",
                borderBottom: "1px solid #a7f3d0",
                padding: "1.25rem 1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  flexShrink: 0,
                }}
              >
                <i className="fa-solid fa-check" />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.15rem",
                    fontWeight: "700",
                    color: "#065f46",
                  }}
                >
                  Certificate Verified
                </h3>
              </div>
            </div>

            {/* Certificate Details */}
            <div style={{ padding: "1.75rem 1.5rem" }}>
              <div style={{ marginBottom: "1.5rem", textAlign: "center" }}>
                <span
                  style={{
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "#64748b",
                    fontWeight: "600",
                  }}
                >
                  Issued To
                </span>
                <h1
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "1.6rem",
                    fontWeight: "800",
                    color: "#0f172a",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {result.certificate?.studentName}
                </h1>
              </div>

              <div
                style={{
                  backgroundColor: "#f8fafc",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  padding: "1.25rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.9rem",
                  fontSize: "0.9rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#64748b", fontWeight: "500" }}>
                    Awarded for:
                  </span>
                  <span style={{ color: "#0f172a", fontWeight: "700" }}>
                    {result.certificate?.templateName}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#64748b", fontWeight: "500" }}>
                    Issued By:
                  </span>
                  <span style={{ color: "#0f172a", fontWeight: "700" }}>
                    {result.certificate?.instituteName}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#64748b", fontWeight: "500" }}>
                    Issue Date:
                  </span>
                  <span style={{ color: "#0f172a", fontWeight: "600" }}>
                    {new Date(
                      result.certificate?.issuedAt || Date.now(),
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>
              </div>

              {/* Authenticity Guarantee Footer */}
              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "0.85rem",
                  borderRadius: "8px",
                  backgroundColor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "0.82rem",
                  color: "#475569",
                }}
              >
                <i
                  className="fa-solid fa-shield-check"
                  style={{
                    color: "var(--color-3, #8dc63f)",
                    fontSize: "1.2rem",
                  }}
                />
                <span>
                  This digital credential is valid and recorded in the secure
                  GradeMe AI registry.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
